import axios from "axios";
import { clearDirty } from "@/lib/unsavedChanges";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API, withCredentials: true });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("aos_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// A successful write means the page's pending edits were saved (lib/unsavedChanges.js). AI suggestions are
// POSTs too but save nothing, so they do not count.
const WRITES = new Set(["post", "put", "patch", "delete"]);
api.interceptors.response.use((response) => {
  const { method, url } = response.config || {};
  if (WRITES.has((method || "").toLowerCase()) && !/\/ai\//.test(url || "")) clearDirty();
  return response;
});

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Terjadi kesalahan. Coba lagi.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  // Refusals that list what is missing ({message, blockers}) — e.g. a KKA status change — show the list.
  if (detail && typeof detail.message === "string") {
    const blockers = Array.isArray(detail.blockers) ? detail.blockers.filter((b) => typeof b === "string" && b.trim()) : [];
    return blockers.length ? `${detail.message} ${blockers.join(" · ")}` : detail.message;
  }
  return String(detail);
}

// Extracts a meaningful, user-facing message from an axios error, distinguishing
// backend validation errors, HTTP status codes, and network/gateway failures.
export function getErrorMessage(error) {
  if (error?.response) {
    const d = error.response.data?.detail;
    if (d != null) return formatApiErrorDetail(d);
    const status = error.response.status;
    if (status === 401) return "Sesi tidak valid atau kredensial salah.";
    if (status === 403) return "Akses ditolak.";
    if ([502, 503, 504].includes(status))
      return `Server sedang dimuat ulang (${status}). Mohon coba lagi dalam beberapa detik.`;
    return `Terjadi kesalahan pada server (${status}).`;
  }
  if (error?.request)
    return "Tidak dapat terhubung ke server. Periksa koneksi Anda lalu coba lagi.";
  return error?.message || "Terjadi kesalahan. Coba lagi.";
}

export const rupiah = (n) =>
  n == null ? "-" : "Rp " + Number(n).toLocaleString("id-ID");

export async function downloadFile(path, filename) {
  const token = localStorage.getItem("aos_token");
  const res = await fetch(`${API}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    credentials: "include",
  });
  if (!res.ok) {
    // Keep the server's reason (e.g. why an export was refused) instead of a generic message.
    let detail = null;
    try { detail = (await res.json())?.detail ?? null; } catch { /* Non-JSON error bodies keep the generic message. */ }
    const error = new Error(detail != null ? formatApiErrorDetail(detail) : "Gagal mengunduh");
    error.response = { status: res.status, data: detail != null ? { detail } : {} };
    throw error;
  }
  const blob = await res.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  const encodedName = res.headers?.get("Content-Disposition")?.match(/(?:^|;)\s*filename\*=UTF-8''([^;]+)/i)?.[1];
  let downloadName = filename;
  if (encodedName) {
    try {
      const supplied = decodeURIComponent(encodedName);
      if (supplied && !/[\x00-\x1f\x7f/\\]/.test(supplied)) downloadName = supplied;
    } catch { /* Keep the caller's name if an older server sends an invalid parameter. */ }
  }
  a.href = url; a.download = downloadName;
  document.body.appendChild(a); a.click(); a.remove();
  window.URL.revokeObjectURL(url);
}

export async function uploadEvidenceChunked(eid, file, meta, opts = {}) {
  const init = await api.post(`/engagements/${eid}/evidence/upload/init`, {
    filename: file.name, size: file.size, meta,
    replace_evidence_id: opts.replaceId || null, replacement_reason: opts.replacementReason || "",
  });
  const { upload_id, chunk_size } = init.data;
  const total = Math.max(1, Math.ceil(file.size / chunk_size));
  for (let i = 0; i < total; i++) {
    const blob = file.slice(i * chunk_size, (i + 1) * chunk_size);
    await api.put(`/evidence/upload/${upload_id}/chunk/${i}`, blob, {
      headers: { "Content-Type": "application/octet-stream" },
    });
    if (opts.onProgress) opts.onProgress(Math.round(((i + 1) / total) * 100));
  }
  const done = await api.post(`/evidence/upload/${upload_id}/complete`);
  return done.data;
}

export const humanSize = (b) =>
  b == null ? "-" : b < 1024 ? `${b} B` : b < 1048576 ? `${(b / 1024).toFixed(1)} KB` : `${(b / 1048576).toFixed(2)} MB`;

export default api;
